import type {
  GitRunStackedActionResult,
  GitStatusResult,
  ScopedThreadRef,
} from "@cmmd-forge/contracts";
import { useCallback, useEffect } from "react";

import {
  resolveLiveThreadBranchUpdate,
  resolveThreadBranchUpdate,
} from "./GitActionsControl.logic";
import { readEnvironmentApi } from "~/environmentApi";
import { newCommandId } from "~/lib/utils";
import { useComposerDraftStore } from "~/composerDraftStore";
import type { DraftId } from "~/draftId";

interface ThreadLike {
  readonly branch: string | null;
  readonly worktreePath: string | null;
}

interface UseGitThreadBranchSyncInput {
  readonly activeThreadRef: ScopedThreadRef | null;
  readonly draftId: DraftId | undefined;
  readonly activeServerThread: ThreadLike | null | undefined;
  readonly activeDraftThread: ThreadLike | null | undefined;
  readonly gitStatus: GitStatusResult | null;
  readonly isGitActionRunning: boolean;
  readonly isSelectingWorktreeBase: boolean;
  readonly setThreadBranch: (
    threadRef: ScopedThreadRef,
    branch: string | null,
    worktreePath: string | null,
  ) => void;
  readonly setDraftThreadContext: ReturnType<
    typeof useComposerDraftStore.getState
  >["setDraftThreadContext"];
}

/** Passive git-status reads describe the selected cwd, not necessarily this thread's worktree. */
export function shouldSyncLiveThreadBranch(input: {
  readonly worktreePath: string | null;
  readonly isGitActionRunning: boolean;
  readonly isSelectingWorktreeBase: boolean;
}): boolean {
  return input.worktreePath === null && !input.isGitActionRunning && !input.isSelectingWorktreeBase;
}

/** Resolve a passive branch update only for a local checkout without an active git action. */
function resolvePassiveBranchUpdate(input: {
  readonly worktreePath: string | null;
  readonly isGitActionRunning: boolean;
  readonly isSelectingWorktreeBase: boolean;
  readonly threadBranch: string | null;
  readonly gitStatus: GitStatusResult | null;
}): { readonly branch: string | null } | null {
  if (!shouldSyncLiveThreadBranch(input)) return null;
  return resolveLiveThreadBranchUpdate({
    threadBranch: input.threadBranch,
    gitStatus: input.gitStatus,
  });
}

export function useGitThreadBranchSync(input: UseGitThreadBranchSyncInput) {
  const persistThreadBranchSync = useCallback(
    (branch: string | null) => {
      if (!input.activeThreadRef) return;

      if (input.activeServerThread) {
        persistServerThreadBranch({ ...input, branch });
        return;
      }

      if (!input.activeDraftThread || input.activeDraftThread.branch === branch) return;
      input.setDraftThreadContext(input.draftId ?? input.activeThreadRef, {
        branch,
        worktreePath: input.activeDraftThread.worktreePath,
      });
    },
    [input],
  );

  const syncThreadBranchAfterGitAction = useCallback(
    (result: GitRunStackedActionResult) => {
      const branchUpdate = resolveThreadBranchUpdate(result);
      if (branchUpdate) {
        persistThreadBranchSync(branchUpdate.branch);
      }
    },
    [persistThreadBranchSync],
  );

  const activeWorktreePath =
    input.activeServerThread?.worktreePath ?? input.activeDraftThread?.worktreePath ?? null;
  const activeThreadBranch =
    input.activeServerThread?.branch ?? input.activeDraftThread?.branch ?? null;

  useEffect(() => {
    const branchUpdate = resolvePassiveBranchUpdate({
      worktreePath: activeWorktreePath,
      isGitActionRunning: input.isGitActionRunning,
      isSelectingWorktreeBase: input.isSelectingWorktreeBase,
      threadBranch: activeThreadBranch,
      gitStatus: input.gitStatus,
    });
    if (branchUpdate) {
      persistThreadBranchSync(branchUpdate.branch);
    }
  }, [
    input.activeDraftThread?.branch,
    input.activeDraftThread?.worktreePath,
    input.activeServerThread?.branch,
    input.activeServerThread?.worktreePath,
    input.gitStatus,
    input.isGitActionRunning,
    input.isSelectingWorktreeBase,
    persistThreadBranchSync,
    activeThreadBranch,
    activeWorktreePath,
  ]);

  return { syncThreadBranchAfterGitAction };
}

function persistServerThreadBranch(
  input: UseGitThreadBranchSyncInput & { readonly branch: string | null },
): void {
  if (!input.activeThreadRef || !input.activeServerThread) return;
  if (input.activeServerThread.branch === input.branch) return;

  const worktreePath = input.activeServerThread.worktreePath;
  const api = readEnvironmentApi(input.activeThreadRef.environmentId);
  if (api) {
    void api.orchestration
      .dispatchCommand({
        type: "thread.meta.update",
        commandId: newCommandId(),
        threadId: input.activeThreadRef.threadId,
        branch: input.branch,
        worktreePath,
      })
      .catch(() => undefined);
  }

  input.setThreadBranch(input.activeThreadRef, input.branch, worktreePath);
}
